import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Phone, MapPin, Wrench, Plus, User, Trash2, Siren, CheckCircle2 } from "lucide-react";

export default function Safety() {
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", relationship: "" });
  const [sosActive, setSosActive] = useState(false);
  const [sosData, setSosData] = useState(null);

  const load = () => api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addContact = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone required."); return; }
    try { await api.post("/contacts", form); toast.success("Contact added."); setOpen(false); setForm({ name: "", phone: "", relationship: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delContact = async (id) => { await api.delete(`/contacts/${id}`); load(); };

  const triggerSos = async () => {
    try {
      const { data } = await api.post("/sos");
      setSosData(data); setSosActive(true);
      toast.success("SOS activated. We've got you covered.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="safety-page">
      <PageHeader title="Safety & Emergency" subtitle="Help is one tap away." icon={ShieldAlert} />

      {/* SOS button */}
      <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
        <button data-testid="sos-button" onClick={triggerSos}
          className="vp-pulse-ring relative mx-auto grid h-32 w-32 place-items-center rounded-full bg-red-500 text-white shadow-xl shadow-red-500/40 transition-transform active:scale-95">
          <div className="flex flex-col items-center">
            <Siren size={40} />
            <span className="mt-1 font-display text-xl font-extrabold">SOS</span>
          </div>
        </button>
        <p className="mt-6 text-sm text-red-700">Tap to alert your contacts & get emergency guidance.</p>
      </div>

      {sosActive && sosData && (
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 vp-fade-up" data-testid="sos-result">
          <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 size={20} /><span className="font-semibold">{sosData.message}</span></div>
          {sosData.contacts_notified?.length > 0 && (
            <p className="mt-2 text-sm text-zinc-600">Notified: {sosData.contacts_notified.join(", ")}</p>
          )}
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {sosData.emergency_numbers.map((n) => (
              <a key={n.number} href={`tel:${n.number}`} className="flex items-center gap-2 rounded-2xl border border-zinc-200 p-3 text-sm hover:bg-zinc-50">
                <Phone size={16} className="text-red-500" /><div><div className="font-medium">{n.number}</div><div className="text-xs text-zinc-400">{n.name}</div></div>
              </a>
            ))}
          </div>
          <h4 className="mt-5 mb-2 font-semibold text-sm">What to do now</h4>
          <ol className="space-y-1.5 text-sm text-zinc-600">
            {sosData.checklist.map((c, i) => <li key={i} className="flex gap-2"><span className="text-emerald-500 font-bold">{i + 1}.</span> {c}</li>)}
          </ol>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-4">
        <a href="tel:112" className="flex flex-col items-center gap-2 rounded-3xl border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow" data-testid="call-emergency">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-red-50 text-red-600"><Phone size={22} /></div>
          <span className="text-sm font-medium">Call 112</span>
        </a>
        <button onClick={() => toast.info("Roadside assistance request sent (demo).")} className="flex flex-col items-center gap-2 rounded-3xl border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow" data-testid="roadside">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-amber-50 text-amber-600"><Wrench size={22} /></div>
          <span className="text-sm font-medium">Roadside Help</span>
        </button>
      </div>

      {/* Emergency contacts */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display font-semibold">Emergency contacts</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-contact-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 gap-1.5"><Plus size={16} /> Add</Button></DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle>Add emergency contact</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" /></div>
                <div><Label>Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl" /></div>
                <div><Label>Relationship</Label><Input data-testid="contact-rel" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="Spouse, brother…" className="mt-1.5 h-11 rounded-xl" /></div>
              </div>
              <DialogFooter><Button data-testid="contact-save" onClick={addContact} className="rounded-full bg-emerald-500 hover:bg-emerald-600">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {contacts.length === 0 ? (
          <EmptyState icon={User} title="No contacts yet" text="Add someone we can alert in an emergency." />
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="contact-item">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"><User size={18} /></div>
                <div className="flex-1"><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-zinc-400">{c.phone} {c.relationship && `• ${c.relationship}`}</div></div>
                <a href={`tel:${c.phone}`} className="grid h-9 w-9 place-items-center rounded-full bg-emerald-50 text-emerald-600"><Phone size={16} /></a>
                <button onClick={() => delContact(c.id)} className="text-zinc-300 hover:text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
